/**
 * Leave Management API client
 */

import apiClient from '@/core/api/client'
import { API_ENDPOINTS } from '@/core/api/endpoints'
import type {
  LeaveType,
  LeaveBalance,
  LeaveRequest,
  CreateLeaveRequestInput,
  UpdateLeaveRequestInput,
  ApprovalActionInput,
  CreateLeaveTypeInput,
  UpdateLeaveTypeInput,
  PublicHoliday,
  CreatePublicHolidayInput,
  UpdatePublicHolidayInput,
  BulkCreatePublicHolidayInput,
  BulkCreatePublicHolidayResponse,
  LeaveReportFilters,
  ComprehensiveLeaveReport,
  ReportEmployee,
  ReportDepartment,
  LeaveRequestForReport,
  Pagination,
} from '../types/leave.types'
import type { ApiResponse, PaginatedResponse } from '@/shared/types/common.types'

export const leaveApi = {
  // Leave Types
  getLeaveTypes: async () => {
    const response = await apiClient.get<ApiResponse<LeaveType[]>>(
      API_ENDPOINTS.LEAVE.TYPES
    )
    return response.data
  },

  // Leave Balances
  getLeaveBalances: async (year?: number) => {
    const params = year ? { year } : {}
    const response = await apiClient.get<ApiResponse<LeaveBalance[]>>(
      API_ENDPOINTS.LEAVE.BALANCES,
      { params }
    )
    return response.data
  },

  // Leave Requests
  getLeaveRequests: async (params?: {
    status?: string
    page?: number
    limit?: number
  }) => {
    const response = await apiClient.get<PaginatedResponse<LeaveRequest>>(
      API_ENDPOINTS.LEAVE.REQUESTS,
      { params }
    )
    return response.data
  },

  getLeaveRequest: async (id: string) => {
    const response = await apiClient.get<ApiResponse<LeaveRequest>>(
      API_ENDPOINTS.LEAVE.REQUEST(id)
    )
    return response.data
  },

  createLeaveRequest: async (data: CreateLeaveRequestInput) => {
    const response = await apiClient.post<ApiResponse<LeaveRequest>>(
      API_ENDPOINTS.LEAVE.REQUESTS,
      data
    )
    return response.data
  },

  updateLeaveRequest: async (id: string, data: UpdateLeaveRequestInput) => {
    const response = await apiClient.put<ApiResponse<LeaveRequest>>(
      API_ENDPOINTS.LEAVE.REQUEST(id),
      data
    )
    return response.data
  },

  approveLeaveRequest: async (id: string, data?: ApprovalActionInput) => {
    const response = await apiClient.post<ApiResponse<LeaveRequest>>(
      API_ENDPOINTS.LEAVE.APPROVE(id),
      data || {}
    )
    return response.data
  },

  rejectLeaveRequest: async (id: string, data?: ApprovalActionInput) => {
    const response = await apiClient.post<ApiResponse<LeaveRequest>>(
      API_ENDPOINTS.LEAVE.REJECT(id),
      data || {}
    )
    return response.data
  },

  cancelLeaveRequest: async (id: string) => {
    const response = await apiClient.post<ApiResponse<LeaveRequest>>(
      API_ENDPOINTS.LEAVE.REQUEST(id) + '/cancel'
    )
    return response.data
  },

  // Admin Leave Type Management
  getAdminLeaveTypes: async (includeInactive = true) => {
    const response = await apiClient.get<ApiResponse<LeaveType[]>>(
      API_ENDPOINTS.LEAVE.ADMIN_TYPES,
      { params: { include_inactive: includeInactive } }
    )
    return response.data
  },

  getAdminLeaveType: async (id: string) => {
    const response = await apiClient.get<ApiResponse<LeaveType>>(
      API_ENDPOINTS.LEAVE.ADMIN_TYPE(id)
    )
    return response.data
  },

  createLeaveType: async (data: CreateLeaveTypeInput) => {
    const response = await apiClient.post<ApiResponse<LeaveType>>(
      API_ENDPOINTS.LEAVE.ADMIN_TYPES,
      data
    )
    return response.data
  },

  updateLeaveType: async (id: string, data: UpdateLeaveTypeInput) => {
    const response = await apiClient.put<ApiResponse<LeaveType>>(
      API_ENDPOINTS.LEAVE.ADMIN_TYPE(id),
      data
    )
    return response.data
  },

  deleteLeaveType: async (id: string) => {
    const response = await apiClient.delete<{ message: string }>(
      API_ENDPOINTS.LEAVE.ADMIN_TYPE(id)
    )
    return response.data
  },

  // Public Holidays (for all users)
  getPublicHolidays: async (year?: number) => {
    const params = year ? { year } : {}
    const response = await apiClient.get<ApiResponse<PublicHoliday[]>>(
      API_ENDPOINTS.LEAVE.HOLIDAYS,
      { params }
    )
    return response.data
  },

  // Admin Public Holiday Management
  getAdminHolidays: async (params?: { year?: number; include_inactive?: boolean }) => {
    const response = await apiClient.get<ApiResponse<PublicHoliday[]>>(
      API_ENDPOINTS.LEAVE.ADMIN_HOLIDAYS,
      { params }
    )
    return response.data
  },

  getAdminHoliday: async (id: string) => {
    const response = await apiClient.get<ApiResponse<PublicHoliday>>(
      API_ENDPOINTS.LEAVE.ADMIN_HOLIDAY(id)
    )
    return response.data
  },

  createHoliday: async (data: CreatePublicHolidayInput) => {
    const response = await apiClient.post<ApiResponse<PublicHoliday>>(
      API_ENDPOINTS.LEAVE.ADMIN_HOLIDAYS,
      data
    )
    return response.data
  },

  createHolidaysBulk: async (data: BulkCreatePublicHolidayInput): Promise<BulkCreatePublicHolidayResponse> => {
    const response = await apiClient.post<BulkCreatePublicHolidayResponse>(
      `${API_ENDPOINTS.LEAVE.ADMIN_HOLIDAYS}/bulk`,
      data
    )
    return response.data
  },

  updateHoliday: async (id: string, data: UpdatePublicHolidayInput) => {
    const response = await apiClient.put<ApiResponse<PublicHoliday>>(
      API_ENDPOINTS.LEAVE.ADMIN_HOLIDAY(id),
      data
    )
    return response.data
  },

  deleteHoliday: async (id: string) => {
    const response = await apiClient.delete<{ message: string }>(
      API_ENDPOINTS.LEAVE.ADMIN_HOLIDAY(id)
    )
    return response.data
  },

  // Leave Calendar (Admin/HR)
  getLeaveCalendar: async (year: number, month: number) => {
    const response = await apiClient.get<ApiResponse<LeaveRequest[]>>(
      API_ENDPOINTS.LEAVE.CALENDAR,
      { params: { year, month } }
    )
    return response.data
  },

  // ==================== Reports ====================

  getComprehensiveReport: async (filters?: LeaveReportFilters): Promise<{ data: ComprehensiveLeaveReport }> => {
    const response = await apiClient.get<{ data: ComprehensiveLeaveReport }>(
      API_ENDPOINTS.LEAVE.REPORT_COMPREHENSIVE,
      { params: filters }
    )
    return response.data
  },

  getReportEmployees: async (search?: string): Promise<{ data: ReportEmployee[] }> => {
    const params = search ? { search } : {}
    const response = await apiClient.get<{ data: ReportEmployee[] }>(
      API_ENDPOINTS.LEAVE.REPORT_EMPLOYEES,
      { params }
    )
    return response.data
  },

  getReportDepartments: async (): Promise<{ data: ReportDepartment[] }> => {
    const response = await apiClient.get<{ data: ReportDepartment[] }>(
      API_ENDPOINTS.LEAVE.REPORT_DEPARTMENTS
    )
    return response.data
  },

  getReportRequests: async (params?: LeaveReportFilters & {
    page?: number
    limit?: number
    sort_by?: string
    sort_order?: string
  }): Promise<{ data: LeaveRequestForReport[]; pagination: Pagination }> => {
    const response = await apiClient.get<{ data: LeaveRequestForReport[]; pagination: Pagination }>(
      API_ENDPOINTS.LEAVE.REPORT_REQUESTS,
      { params }
    )
    return response.data
  },
}
